import { type OverlayConfig, Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { DialogOverlayRegistry } from '@core/dialog/dialog-overlay-registry';
import type { Observable } from 'rxjs';
import { filter, Subject } from 'rxjs';
import { z } from 'zod';
import { ConfirmationDialogComponent } from '../components/confirmation-dialog/confirmation-dialog.component';

export const confirmationDialogDataSchema = z.object({
  title: z.string().min(1),
  message: z.string().min(1),
  confirmText: z.string().optional(),
  cancelText: z.string().min(1),
  showConfirm: z.boolean().optional(),
  showDiscard: z.boolean().optional(),
  discardText: z.string().optional(),
});

export type ConfirmationDialogData = z.infer<
  typeof confirmationDialogDataSchema
>;

export type ConfirmationResult = 'confirm' | 'discard' | 'cancel';

function localizeDialogText(value: string | undefined): string | undefined {
  switch (value) {
    case undefined:
      return undefined;
    case 'shared.button.cancel':
      return $localize`Cancel`;
    case 'shared.button.delete':
      return $localize`Delete`;
    case 'shared.button.discard':
      return $localize`Discard`;
    case 'shared.changes.unsavedTitle':
      return $localize`Unsaved changes`;
    case 'shared.changes.unsavedMessageSingle':
      return $localize`You have 1 unsaved change. Do you want to save it before continuing?`;
    case 'shared.changes.unsavedMessagePlural':
      return $localize`You have unsaved changes. Do you want to save them before continuing?`;
    case 'shared.changes.save':
      return $localize`Save`;
    case 'shared.changes.discard':
      return $localize`Discard changes`;
    case 'ruleHandling.rules.deleteConfirmTitle':
      return $localize`Delete rule?`;
    case 'ruleHandling.rules.deleteConfirmMessage':
      return $localize`This cannot be undone.`;
    case 'ruleHandling.rules.reinheritConfirmTitle':
      return $localize`Remove local override?`;
    case 'ruleHandling.rules.reinheritConfirmMessage':
      return $localize`This restores the inherited rule set for the node.`;
    case 'ruleHandling.ruleset.deleteConfirmTitle':
      return $localize`Delete rule set?`;
    case 'ruleHandling.ruleset.deleteConfirmMessage':
      return $localize`This deletes the whole rule set and cannot be undone.`;
    case 'ruleHandling.groups.deleteConfirmTitle':
      return $localize`Delete group?`;
    case 'ruleHandling.groups.deleteConfirmMessage':
      return $localize`This removes the selected group and all subgroups and rules.`;
    case 'ruleHandling.rules.overrideNeedsRuleSetTitle':
      return $localize`Create a local rule set?`;
    case 'ruleHandling.rules.overrideNeedsRuleSetMessage':
      return $localize`To override an inherited rule, you first need to create a local rule set.`;
    case 'ruleHandling.details.addRuleSet':
      return $localize`Add new rule set`;
    case 'ruleHandling.versionPanel.discardDraftTitle':
      return $localize`Discard draft?`;
    case 'ruleHandling.versionPanel.discardDraftMessage':
      return $localize`This permanently deletes the draft version. The active version is not affected.`;
    default:
      return value;
  }
}

@Injectable({
  providedIn: 'root',
})
export class ConfirmationDialogService {
  private overlay = inject(Overlay);
  readonly #router = inject(Router);
  readonly #dialogOverlayRegistry = inject(DialogOverlayRegistry);

  openDialog(data: ConfirmationDialogData): Observable<ConfirmationResult> {
    const validatedData = confirmationDialogDataSchema.parse({
      ...data,
      title: localizeDialogText(data.title),
      message: localizeDialogText(data.message),
      confirmText: localizeDialogText(data.confirmText),
      cancelText: localizeDialogText(data.cancelText),
      discardText: localizeDialogText(data.discardText),
    });

    const overlayRef = this.overlay.create(this.getOverlayConfig());
    const dialogPortal = new ComponentPortal(ConfirmationDialogComponent);
    const componentRef = overlayRef.attach(dialogPortal);

    componentRef.setInput('data', validatedData);

    const result$ = new Subject<ConfirmationResult>();
    let isClosed = false;
    const close = (result: ConfirmationResult) => {
      if (isClosed) {
        return;
      }

      isClosed = true;
      navigationSubscription.unsubscribe();
      unregister();
      result$.next(result);
      result$.complete();
      overlayRef.dispose();
    };
    const unregister = this.#dialogOverlayRegistry.register(() => close('cancel'));
    const navigationSubscription = this.#router.events.pipe(
      filter((event): event is NavigationStart => event instanceof NavigationStart),
    ).subscribe(() => close('cancel'));

    componentRef.instance.confirm.subscribe(() => {
      close('confirm');
    });

    componentRef.instance.discard.subscribe(() => {
      close('discard');
    });

    componentRef.instance.cancel.subscribe(() => {
      close('cancel');
    });

    overlayRef.backdropClick().subscribe(() => {
      close('cancel');
    });

    overlayRef.keydownEvents().subscribe((event) => {
      if (event.key === 'Escape') {
        close('cancel');
      }
    });

    return result$.asObservable();
  }

  private getOverlayConfig(): OverlayConfig {
    return {
      positionStrategy: this.overlay
        .position()
        .global()
        .centerHorizontally()
        .centerVertically(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-dark-backdrop',
      scrollStrategy: this.overlay.scrollStrategies.block(),
    };
  }

  confirmUnsavedChanges(
    count: number,
    options?: { showSave?: boolean },
  ): Observable<ConfirmationResult> {
    const showSave = options?.showSave ?? true;

    return this.openDialog({
      title: 'shared.changes.unsavedTitle',
      message:
        count === 1
          ? 'shared.changes.unsavedMessageSingle'
          : 'shared.changes.unsavedMessagePlural',
      confirmText: showSave ? 'shared.changes.save' : undefined,
      cancelText: 'shared.button.cancel',
      showConfirm: showSave,
      showDiscard: true,
      discardText: 'shared.changes.discard',
    });
  }
}
